import { motion } from "framer-motion";
import { User, CalendarCheck, Wallet, FileCheck, MessageSquare, ClipboardCheck, Landmark, Copy, ExternalLink, Users, BookOpen, Trophy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/gestionale/components/ui/card";
import { Badge } from "@/gestionale/components/ui/badge";
import { Button } from "@/gestionale/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/gestionale/integrations/supabase/client";
import { useAuth } from "@/gestionale/hooks/useAuth";
import { format, parseISO, isBefore } from "date-fns";
import { it } from "date-fns/locale";
import { RateListCard } from "@/gestionale/components/atleta/RateListCard";
import { ChangePasswordDialog } from "@/gestionale/components/ChangePasswordDialog";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { usePagamentoOnline } from "@/gestionale/hooks/usePagamentoOnline";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" as const } },
};

export default function AtletaDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { attivo: pagamentoOnlineAttivo } = usePagamentoOnline();

  // Handle payment result from Stripe redirect
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast.success("Pagamento completato con successo!");
      searchParams.delete("payment");
      searchParams.delete("rata_id");
      setSearchParams(searchParams, { replace: true });
    } else if (paymentStatus === "cancelled") {
      toast.info("Pagamento annullato");
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Get persona linked to this user
  const { data: persona } = useQuery({
    queryKey: ["atleta-persona", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("persone")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  // Ruoli della persona
  const { data: ruoliPersona = [] } = useQuery({
    queryKey: ["atleta-ruoli", persona?.id],
    enabled: !!persona,
    queryFn: async () => {
      const { data } = await supabase.from("ruoli").select("tipo_ruolo").eq("persona_id", persona!.id);
      return (data || []).map((r: any) => r.tipo_ruolo);
    },
  });

  const isGiocatore = ruoliPersona.includes("Giocatore");

  // Get abbonamenti for this persona
  const { data: abbonamenti, refetch: refetchAbbonamenti } = useQuery({
    queryKey: ["atleta-abbonamenti", persona?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("abbonamenti")
        .select("*")
        .eq("persona_id", persona!.id)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: !!persona,
  });

  // Get rate for abbonamenti
  const { data: rate, refetch: refetchRate } = useQuery({
    queryKey: ["atleta-rate", persona?.id],
    queryFn: async () => {
      const abbIds = (abbonamenti || []).map((a) => a.id);
      if (abbIds.length === 0) return [];
      const { data } = await supabase
        .from("rate")
        .select("*")
        .in("abbonamento_id", abbIds)
        .order("data_scadenza", { ascending: true });
      return data || [];
    },
    enabled: !!abbonamenti && abbonamenti.length > 0,
  });

  // Refetch after payment success
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      refetchAbbonamenti();
      refetchRate();
    }
  }, [searchParams, refetchAbbonamenti, refetchRate]);

  // Get movimenti (pagamenti) for this persona
  const { data: movimenti } = useQuery({
    queryKey: ["atleta-movimenti", persona?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("movimenti")
        .select("*")
        .eq("persona_id", persona!.id)
        .order("data", { ascending: false })
        .limit(20);
      return data || [];
    },
    enabled: !!persona,
  });

  // Movimenti collegati agli abbonamenti dell'atleta (per stato pagamento derivato)
  const { data: movimentiAbb } = useQuery({
    queryKey: ["atleta-movimenti-abbonamenti", persona?.id, (abbonamenti || []).map((a) => a.id).join(",")],
    queryFn: async () => {
      const abbIds = (abbonamenti || []).map((a) => a.id);
      if (abbIds.length === 0) return [];
      const { data } = await supabase
        .from("movimenti")
        .select("riferimento_id")
        .eq("riferimento_tipo", "abbonamento")
        .eq("tipo", "Entrata")
        .in("riferimento_id", abbIds);
      return data || [];
    },
    enabled: !!abbonamenti && abbonamenti.length > 0,
  });

  const abbPagatiSet = new Set((movimentiAbb || []).map((m) => m.riferimento_id).filter(Boolean) as string[]);

  // Get comunicazioni sent to this persona or their corso
  const { data: comunicazioni } = useQuery({
    queryKey: ["atleta-comunicazioni", persona?.id],
    queryFn: async () => {
      // Get comunicazioni where persona is a destinatario
      const { data: destData } = await supabase
        .from("comunicazioni_destinatari")
        .select("comunicazione_id")
        .eq("persona_id", persona!.id);

      const commIds = (destData || []).map((d) => d.comunicazione_id);

      // Also get comunicazioni sent to "tutti" or to their corsi
      const corsi = (abbonamenti || []).map((a) => a.corso);
      
      const { data } = await supabase
        .from("comunicazioni")
        .select("*")
        .or(
          [
            commIds.length > 0 ? `id.in.(${commIds.join(",")})` : null,
            `tipo_destinatari.eq.tutti`,
            ...corsi.map((c) => `corso_filtro.eq.${c}`),
          ]
            .filter(Boolean)
            .join(",")
        )
        .order("created_at", { ascending: false })
        .limit(10);

      return data || [];
    },
    enabled: !!persona && !!abbonamenti,
  });

  // Statistiche partite (solo per giocatori)
  const { data: statPartite = [] } = useQuery({
    queryKey: ["atleta-stat-partite", persona?.id],
    enabled: !!persona && isGiocatore,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("statistiche_partita")
        .select("*, partita:partite(data, avversario, casa_trasferta, tipo, gol_fatti, gol_subiti)")
        .eq("persona_id", persona!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const totalePartite = statPartite.filter((s: any) => s.minuti_giocati > 0).length;
  const totaleGoal = statPartite.reduce((sum: number, s: any) => sum + (s.goal || 0), 0);
  const totaleAmmonizioni = statPartite.reduce((sum: number, s: any) => sum + (s.ammonizioni || 0), 0);
  const totaleEspulsioni = statPartite.reduce((sum: number, s: any) => sum + (s.espulsioni || 0), 0);

  // Presenze stats
  const { data: presenzeStats } = useQuery({
    queryKey: ["atleta-presenze", persona?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("presenze")
        .select("presente, sessione_id")
        .eq("persona_id", persona!.id);
      const totale = (data || []).length;
      const presenti = (data || []).filter((p) => p.presente).length;
      return { totale, presenti, percentuale: totale > 0 ? Math.round((presenti / totale) * 100) : 0 };
    },
    enabled: !!persona,
  });

  // Get payment info settings
  const { data: paymentInfo } = useQuery({
    queryKey: ["payment-info-atleta"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("impostazioni_generali")
        .select("chiave, valore")
        .like("chiave", "pagamento_%");
      if (error) throw error;
      const map: Record<string, string> = {};
      (data || []).forEach((s) => { map[s.chiave] = s.valore || ""; });
      return map;
    },
  });

  // Corsi iscritti
  const { data: corsiIscritti = [] } = useQuery({
    queryKey: ["atleta-corsi", persona?.id],
    enabled: !!persona,
    queryFn: async () => {
      const { data } = await supabase
        .from("persona_corsi")
        .select("corso:corsi(nome, descrizione)")
        .eq("persona_id", persona!.id);
      return (data || []).map((pc: any) => pc.corso).filter(Boolean);
    },
  });

  const { data: figli = [] } = useQuery({
    queryKey: ["figli", persona?.id],
    enabled: !!persona,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("familiari")
        .select("*, figlio:persone!familiari_figlio_id_fkey(id, nome, cognome, data_nascita, certificato_medico_scadenza)")
        .eq("genitore_id", persona!.id);
      const figli = data || [];

      // Per ogni figlio carica i corsi iscritti
      for (const f of figli) {
        if (f.figlio?.id) {
          const { data: pcData } = await supabase
            .from("persona_corsi")
            .select("corso:corsi(nome)")
            .eq("persona_id", f.figlio.id);
          f.figlio.corsi = (pcData || []).map((pc: any) => pc.corso?.nome).filter(Boolean);
        }
      }
      return figli;
    },
  });

  const hasPaymentInfo = paymentInfo && pagamentoOnlineAttivo && (paymentInfo.pagamento_iban || paymentInfo.pagamento_intestatario || paymentInfo.pagamento_paypal_link || paymentInfo.pagamento_satispay_link);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiato negli appunti");
  };

  const today = new Date();
  const certScaduto = persona?.certificato_medico_scadenza
    ? isBefore(parseISO(persona.certificato_medico_scadenza), today)
    : false;

  if (!persona) {
    return (
      <div className="space-y-8">
        <h1 className="text-3xl font-bold tracking-tight">La mia Area</h1>
        <Card className="glass-card">
          <CardContent className="p-6">
            <p className="text-muted-foreground">
              Il tuo account non è ancora collegato a un profilo atleta. Contatta la segreteria.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group rate by abbonamento
  const rateByAbbonamento = (abbonamenti || []).map((abb) => ({
    abbonamento: abb,
    rate: (rate || []).filter((r) => r.abbonamento_id === abb.id),
  })).filter((item) => item.rate.length > 0);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">La mia Area</h1>
          <p className="text-muted-foreground mt-1">
            Benvenuto, {persona.nome} {persona.cognome}
          </p>
        </div>
        <ChangePasswordDialog />
      </div>

      <motion.div
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Info Personali */}
        <motion.div variants={item} className="lg:col-span-1">
          <Card className="glass-card h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Informazioni Personali
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nome</span>
                <span className="font-medium">{persona.nome} {persona.cognome}</span>
              </div>
              {persona.data_nascita && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Data di nascita</span>
                  <span>{format(parseISO(persona.data_nascita), "dd/MM/yyyy")}</span>
                </div>
              )}
              {persona.codice_fiscale && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Codice Fiscale</span>
                  <span className="font-mono text-xs uppercase">{persona.codice_fiscale}</span>
                </div>
              )}
              {persona.email && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span>{persona.email}</span>
                </div>
              )}
              {persona.telefono && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Telefono</span>
                  <span>{persona.telefono}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Certificato Medico */}
        <motion.div variants={item}>
          <Card className="glass-card h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-primary" />
                Certificato Medico
              </CardTitle>
            </CardHeader>
            <CardContent>
              {persona.certificato_medico_scadenza ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Scadenza</span>
                    <span className="text-sm font-medium">
                      {format(parseISO(persona.certificato_medico_scadenza), "dd/MM/yyyy")}
                    </span>
                  </div>
                  <Badge variant={certScaduto ? "destructive" : "default"}>
                    {certScaduto ? "Scaduto" : "Valido"}
                  </Badge>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nessun certificato registrato</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Presenze */}
        <motion.div variants={item}>
          <Card className="glass-card h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                Presenze
              </CardTitle>
            </CardHeader>
            <CardContent>
              {presenzeStats ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Frequenza</span>
                    <span className="text-2xl font-bold">{presenzeStats.percentuale}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {presenzeStats.presenti} presenze su {presenzeStats.totale} sessioni
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nessuna presenza registrata</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* Rate con pagamento online */}
      {rateByAbbonamento.length > 0 && (
        <motion.div variants={item} initial="hidden" animate="show" className="space-y-4">
          <h2 className="text-xl font-semibold">Le mie Rate</h2>
          {rateByAbbonamento.map(({ abbonamento, rate }) => (
            <RateListCard key={abbonamento.id} rate={rate} abbonamento={abbonamento} pagamentoOnlineAttivo={pagamentoOnlineAttivo} />
          ))}
        </motion.div>
      )}

      {/* Dati per il pagamento */}
      {hasPaymentInfo && (
        <motion.div variants={item} initial="hidden" animate="show">
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Landmark className="h-5 w-5 text-primary" />
                Dati per il Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {paymentInfo.pagamento_intestatario && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Intestatario</span>
                  <span className="font-medium">{paymentInfo.pagamento_intestatario}</span>
                </div>
              )}
              {paymentInfo.pagamento_iban && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-muted-foreground">IBAN</span>
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-xs font-medium">{paymentInfo.pagamento_iban}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(paymentInfo.pagamento_iban)}
                      title="Copia IBAN"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
              {paymentInfo.pagamento_banca && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Banca</span>
                  <span>{paymentInfo.pagamento_banca}</span>
                </div>
              )}
              {paymentInfo.pagamento_causale && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-muted-foreground">Causale</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs">{paymentInfo.pagamento_causale}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(paymentInfo.pagamento_causale)}
                      title="Copia causale"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
              {paymentInfo.pagamento_note && (
                <div className="mt-3 p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground whitespace-pre-line">{paymentInfo.pagamento_note}</p>
                </div>
              )}
              {(paymentInfo.pagamento_paypal_link || paymentInfo.pagamento_satispay_link) && (
                <div className="mt-4 pt-3 border-t flex flex-col gap-2">
                  {paymentInfo.pagamento_paypal_link && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 w-full"
                      onClick={() => window.open(paymentInfo.pagamento_paypal_link, "_blank")}
                    >
                      <ExternalLink className="h-4 w-4" />
                      Paga con PayPal
                    </Button>
                  )}
                  {paymentInfo.pagamento_satispay_link && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 w-full"
                      onClick={() => window.open(paymentInfo.pagamento_satispay_link, "_blank")}
                    >
                      <ExternalLink className="h-4 w-4" />
                      Paga con Satispay
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Abbonamento info */}
        <motion.div variants={item} initial="hidden" animate="show">
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarCheck className="h-5 w-5 text-primary" />
                Abbonamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(abbonamenti || []).length > 0 ? (
                abbonamenti!.map((abb) => (
                  <div key={abb.id} className="space-y-2 text-sm p-3 rounded-lg bg-muted/50">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Corso</span>
                      <span className="font-medium">{abb.corso}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Stagione</span>
                      <span>{abb.stagione}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Importo</span>
                      <span>€ {Number(abb.importo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Stato</span>
                      {(() => {
                        const pagato = abbPagatiSet.has(abb.id);
                        return (
                          <Badge
                            variant={pagato ? "default" : "secondary"}
                            className={pagato ? "bg-green-500/20 text-green-700 hover:bg-green-500/30" : "bg-red-500/15 text-red-700"}
                          >
                            {pagato ? "Pagato" : "Non pagato"}
                          </Badge>
                        );
                      })()}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">Nessun abbonamento attivo</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Pagamenti */}
        <motion.div variants={item} initial="hidden" animate="show">
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                Pagamenti Effettuati
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(movimenti || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessun pagamento registrato</p>
              ) : (
                movimenti!.map((m) => (
                  <div key={m.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/30 transition-colors">
                    <div>
                      <span className="text-sm">{m.categoria}</span>
                      {m.note && <p className="text-xs text-muted-foreground">{m.note}</p>}
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold">
                        € {Number(m.importo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                      </span>
                      <p className="text-xs text-muted-foreground">{format(parseISO(m.data), "dd/MM/yyyy")}</p>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Comunicazioni */}
      <motion.div variants={item} initial="hidden" animate="show">
        <Card className="glass-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Comunicazioni
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(comunicazioni || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna comunicazione</p>
            ) : (
              comunicazioni!.map((c) => (
                <div key={c.id} className="p-4 rounded-lg bg-muted/50 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{c.oggetto}</span>
                    <span className="text-xs text-muted-foreground">
                      {format(parseISO(c.created_at), "dd/MM/yyyy", { locale: it })}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{c.messaggio}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Corsi Iscritti */}
      {corsiIscritti.length > 0 && (
        <motion.div variants={item} initial="hidden" animate="show">
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                I miei Corsi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {corsiIscritti.map((corso: any) => (
                <div key={corso.nome} className="p-3 rounded-lg bg-muted/50 text-sm">
                  <span className="font-medium">{corso.nome}</span>
                  {corso.descrizione && (
                    <p className="text-xs text-muted-foreground mt-1">{corso.descrizione}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Statistiche Giocatore */}
      {isGiocatore && (
        <motion.div variants={item} initial="hidden" animate="show">
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                Le mie Statistiche
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold">{totalePartite}</p>
                  <p className="text-xs text-muted-foreground mt-1">Partite giocate</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold">⚽ {totaleGoal}</p>
                  <p className="text-xs text-muted-foreground mt-1">Goal</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold">🟨 {totaleAmmonizioni}</p>
                  <p className="text-xs text-muted-foreground mt-1">Ammonizioni</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold">🟥 {totaleEspulsioni}</p>
                  <p className="text-xs text-muted-foreground mt-1">Espulsioni</p>
                </div>
              </div>

              {statPartite.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Ultime partite</h4>
                  {statPartite.slice(0, 5).map((s: any) => {
                    const p = s.partita;
                    if (!p) return null;
                    const risultato = `${p.gol_fatti} - ${p.gol_subiti}`;
                    const vinta = p.gol_fatti > p.gol_subiti;
                    const persa = p.gol_fatti < p.gol_subiti;
                    return (
                      <div key={s.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 text-sm">
                        <div>
                          <span className="font-medium">{p.casa_trasferta} vs {p.avversario}</span>
                          <p className="text-xs text-muted-foreground">{format(parseISO(p.data), "dd/MM/yyyy")} · {p.tipo}</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          {s.minuti_giocati > 0 && <span className="text-muted-foreground">{s.minuti_giocati}'</span>}
                          {s.goal > 0 && <span>⚽ {s.goal}</span>}
                          {s.ammonizioni > 0 && <span>🟨</span>}
                          {s.espulsioni > 0 && <span>🟥</span>}
                          <Badge variant="outline" className={`text-xs font-bold ${vinta ? "border-green-500 text-green-700" : persa ? "border-red-500 text-red-700" : ""}`}>
                            {risultato}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Figli / Familiari */}
      {figli.length > 0 && (
        <motion.div variants={item} initial="hidden" animate="show">
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                I miei figli
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {figli.map((f: any) => {
                const certScad = f.figlio?.certificato_medico_scadenza
                  ? isBefore(parseISO(f.figlio.certificato_medico_scadenza), today)
                  : null;
                return (
                  <div key={f.id} className="p-3 rounded-lg bg-muted/50 space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{f.figlio?.cognome} {f.figlio?.nome}</span>
                      <Badge variant="outline" className="text-[10px]">{f.relazione}</Badge>
                    </div>
                    {f.figlio?.data_nascita && (
                      <p className="text-xs text-muted-foreground">
                        Nato il {format(parseISO(f.figlio.data_nascita), "dd/MM/yyyy")}
                      </p>
                    )}
                    {f.figlio?.certificato_medico_scadenza && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Cert. Medico:</span>
                        <Badge variant={certScad ? "destructive" : "default"} className="text-[10px]">
                          {certScad ? "Scaduto" : "Valido"} · {format(parseISO(f.figlio.certificato_medico_scadenza), "dd/MM/yyyy")}
                        </Badge>
                      </div>
                    )}
                    {f.figlio?.corsi?.length > 0 && (
                      <div className="space-y-1 pt-1">
                        {f.figlio.corsi.map((nome: string) => (
                          <div key={nome} className="p-2 rounded-md bg-muted/50 text-xs font-medium">{nome}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
